import React, { useState, useEffect, useCallback } from "react";
import PageWrapperForm from "../../components/PageWrapperForm";
import TablePagination from "../../components/TablePagination";
import { useSession } from "hooks/useSession";
import { fetchWrapper } from "utils/apiFetch";

const API_BASE = "https://pro.mokshiq.in";
const PAGE_SIZE = 25;

function StatusBadge({ ok, yes, no }) {
  return (
    <span
      className={`badge ${
        ok ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"
      }`}
    >
      {ok ? yes : no}
    </span>
  );
}

const AgreementListPage = () => {
  const { role } = useSession();

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyUrl, setSelectedCompanyUrl] = useState("");

  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [scope, setScope] = useState("");
  const [sendingId, setSendingId] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, isError = false) => {
    setToast({ msg, isError });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3200);
  }, []);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompanyUrl) params.set("company_url", selectedCompanyUrl);
      if (search) params.set("search", search);
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));

      const res = await fetchWrapper(`${API_BASE}/agreement_list?${params.toString()}`);

      if (res?.status) {
        setForbidden(false);
        setRows(res.data || []);
        setTotalPages(res.total_pages || 1);
        setTotalCount(res.count || 0);
        setScope(res.scope || "");
        if (!selectedCompanyUrl && Array.isArray(res.companies)) {
          setCompanies(res.companies);
        }
      } else {
        if (/permitted/i.test(res?.message || "")) setForbidden(true);
        showToast(res?.message || "Failed to load agreements", true);
        setRows([]);
      }
    } catch (e) {
      showToast("Error connecting to server", true);
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  }, [selectedCompanyUrl, search, page, showToast]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setPage(1);
  }, [selectedCompanyUrl, search]);

  function handleSearchChange(value) {
    setSearchInput(value);
    window.clearTimeout(handleSearchChange._t);
    handleSearchChange._t = window.setTimeout(() => setSearch(value.trim()), 400);
  }

  async function openAgreement(row) {
    setOpeningId(row.id);
    try {
      const body = new URLSearchParams();
      body.append("user_id", String(row.user_id));
      if (row.company_url) body.append("company_url", row.company_url);

      const res = await fetchWrapper(`${API_BASE}/agreement/link`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!res?.status || !res?.data?.url) {
        showToast(res?.message || "Could not open the agreement", true);
        return;
      }
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      showToast("Error connecting to server", true);
    } finally {
      setOpeningId(null);
    }
  }

  async function openDocument(row) {
    setOpeningId(row.id);
    try {
      const body = new URLSearchParams();
      body.append("user_id", String(row.user_id));
      const res = await fetchWrapper(`${API_BASE}/agreement/link`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res?.status || !res?.data?.url) {
        showToast(res?.message || "Could not open the document", true);
        return;
      }
      const t = new URL(res.data.url).searchParams.get("t");
      window.open(`${API_BASE}/agreement/document?t=${encodeURIComponent(t)}`,
        "_blank", "noopener,noreferrer");
    } catch (e) {
      showToast("Error connecting to server", true);
    } finally {
      setOpeningId(null);
    }
  }

  async function sendWhatsApp(row) {
    setConfirmRow(null);
    setSendingId(row.id);
    try {
      const body = new URLSearchParams();
      body.append("record_id", String(row.id));
 
      const res = await fetchWrapper(`${API_BASE}/agreement/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
 
      if (res?.status) {
        showToast(res.message || "Sent on WhatsApp");
      } else {
        showToast(res?.message || "Could not send on WhatsApp", true);
      }
      loadRows();
    } catch (e) {
      showToast("Could not send on WhatsApp. Please try again.", true);
      loadRows();
    } finally {
      setSendingId(null);
    }
  }

  if (forbidden) {
    return (
      <main>
        <div className="container-fluid">
          <div className="row m-1 mb-3">
            <div className="col-12 d-flex justify-content-between align-items-center">
              <div>
                <h4 className="main-title">Agreement Acceptances</h4>
                <ul className="app-line-breadcrumbs mb-3">
                  <li>
                    <a className="f-s-14 f-w-500 text-primary" href="#">
                      <i className="ph-duotone ph-file-text f-s-16 me-1"></i> Agreement
                    </a>
                  </li>
                  <li className="active">
                    <a className="f-s-14 f-w-500" href="#">Acceptances</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="alert alert-warning m-3">
            Your role does not have access to agreement records.
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pb-5">
      <div className="container-fluid">
        {/* Header Breadcrumbs */}
        <div className="row m-1 mb-3">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="main-title">Agreement Acceptances</h4>
              <ul className="app-line-breadcrumbs mb-3">
                <li>
                  <a className="f-s-14 f-w-500 text-primary" href="#">
                    <i className="ph-duotone ph-file-text f-s-16 me-1"></i> Agreement
                  </a>
                </li>
                <li className="active">
                  <a className="f-s-14 f-w-500" href="#">Acceptances</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Search & Company Filters */}
        <div className="card shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md-4">
                <label className="form-label f-w-500">Company</label>
                <select
                  className="form-select"
                  value={selectedCompanyUrl}
                  onChange={(e) => setSelectedCompanyUrl(e.target.value)}
                >
                  <option value="">All companies</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.website}>
                      {c.company_name} ({c.total_accepted_users})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-5">
                <label className="form-label f-w-500">Search</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="iconoir-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by retailer name or phone"
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-md-3 text-md-end">
                <span className="badge bg-primary text-white px-3 py-2">
                  {totalCount} record{totalCount === 1 ? "" : "s"}
                </span>
                {scope && (
                  <div className="text-muted f-s-12 mt-1">
                    Showing everything visible to: <span className="f-w-500 text-dark">{scope}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Data Table */}
        <div className="card shadow-sm">
          <div className="card-body p-0">
            {rowsLoading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className="alert alert-light text-muted text-center m-4">
                No agreement acceptances found.
              </div>
            ) : (
              <div className="table-responsive app-scroll">
                <table className="table table-bottom-border table-box-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">Retailer</th>
                      <th>Phone</th>
                      {!selectedCompanyUrl && <th>Company</th>}
                      <th>Reference</th>
                      <th className="text-center">Version</th>
                      <th className="text-center">Platform</th>
                      <th className="text-center">Fully Read</th>
                      <th className="text-center">Declarations</th>
                      <th className="text-center">WhatsApp</th>
                      <th>IP Address</th>
                      <th>Accepted At</th>
                      <th className="pe-3 text-end">Agreement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="ps-3 f-w-600 text-dark">{r.user_name}</td>
                        <td className="text-secondary">{r.user_phone}</td>
                        {!selectedCompanyUrl && <td className="text-secondary">{r.company_name}</td>}
                        <td className="text-muted f-s-12 font-monospace">
                          {r.document_number || "—"}
                        </td>
                        <td className="text-center">{r.agreement_version}</td>
                        <td className="text-center">
                          <span className="badge bg-secondary text-white text-capitalize">
                            {r.platform || "—"}
                          </span>
                        </td>
                        <td className="text-center"><StatusBadge ok={r.scrolled_fully} yes="Yes" no="No" /></td>
                        <td className="text-center">
                          <StatusBadge
                            ok={r.all_declarations_checked}
                            yes="Complete"
                            no="Incomplete"
                          />
                        </td>
                        <td className="text-center">
                          {r.whatsapp_status === "sent" ? (
                            <span className="badge bg-success text-white">Sent</span>
                          ) : r.whatsapp_status ? (
                            <span
                              className="badge bg-warning text-dark text-capitalize"
                              title={r.whatsapp_status}
                            >
                              {r.whatsapp_status.replace(/_/g, " ")}
                            </span>
                          ) : (
                            <span className="text-muted f-s-12">—</span>
                          )}
                        </td>
                        <td className="text-muted f-s-12 font-monospace">{r.ip_address || "—"}</td>
                        <td className="text-muted f-s-12">{r.accepted_at || "—"}</td>
                        <td className="pe-3 text-end">
                          <div className="d-inline-flex gap-1 align-items-center justify-content-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              disabled={openingId === r.id}
                              onClick={() => openAgreement(r)}
                              style={{ whiteSpace: "nowrap" }}
                            >
                              <i className="iconoir-eye me-1" />
                              {openingId === r.id ? "Opening…" : "View"}
                            </button>
                            {r.has_pdf && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={openingId === r.id}
                                onClick={() => openDocument(r)}
                                title="The signed PDF exactly as accepted"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                <i className="iconoir-page me-1" />
                                PDF
                              </button>
                            )}
                            {r.has_pdf && (
                              <button
                                type="button"
                                className={`btn btn-sm ${
                                  r.whatsapp_status === "sent"
                                    ? "btn-outline-success"
                                    : "btn-success"
                                }`}
                                disabled={sendingId === r.id}
                                title={`Send the signed PDF to ${r.user_phone} on WhatsApp`}
                                onClick={() => setConfirmRow(r)}
                                style={{ whiteSpace: "nowrap" }}
                              >
                                <i className="bi bi-whatsapp me-1" />
                                {sendingId === r.id
                                  ? "Sending…"
                                  : r.whatsapp_status === "sent"
                                  ? "Resend"
                                  : "Send"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* TablePagination standard component */}
          {rows.length > 0 && (
            <TablePagination
              currentPage={page}
              setCurrentPage={setPage}
              totalPages={totalPages}
              itemsPerPage={PAGE_SIZE}
              totalRecords={totalCount}
            />
          )}
        </div>

        {/* Confirm Modal */}
        {confirmRow && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ background: "rgba(0,0,0,.5)", zIndex: 1070 }}
            onClick={() => setConfirmRow(null)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title f-w-600 d-flex align-items-center">
                    <i className="bi bi-whatsapp text-success me-2 fs-5" />
                    Send Agreement on WhatsApp
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setConfirmRow(null)}
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="bg-light rounded p-3 mb-3">
                    <dl className="row mb-0 f-s-14">
                      <dt className="col-5 text-muted fw-normal">Retailer</dt>
                      <dd className="col-7 mb-2 f-w-600 text-dark">{confirmRow.user_name}</dd>
                      <dt className="col-5 text-muted fw-normal">WhatsApp number</dt>
                      <dd className="col-7 mb-2 font-monospace text-dark">{confirmRow.user_phone}</dd>
                      <dt className="col-5 text-muted fw-normal">Document</dt>
                      <dd className="col-7 mb-0 font-monospace text-dark">
                        {confirmRow.document_number || "—"}
                      </dd>
                    </dl>
                  </div>
                  <p className="text-muted f-s-13 mb-0">
                    The retailer receives the signed PDF exactly as they accepted it.
                    It goes to the number on their profile and nowhere else.
                  </p>
                </div>
                <div className="modal-footer border-top">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setConfirmRow(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => sendWhatsApp(confirmRow)}
                  >
                    Send now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div
            className={`position-fixed bottom-0 start-50 translate-middle-x mb-4 px-4 py-2 rounded text-white shadow ${
              toast.isError ? "bg-danger" : "bg-success"
            }`}
            style={{ zIndex: 1080, fontSize: "14px", fontWeight: "500" }}
          >
            {toast.msg}
          </div>
        )}
      </div>
    </main>
  );
};

export default AgreementListPage;